import { useTranslation } from "react-i18next";
import FormGroupTile from "../../basic/FormGroupTile";
import { Row, Col } from "react-bootstrap";
import toggleArrayEntry from "../../../../utils/toggleArrayEntry";
import { AD_TYPES, SD_BID_TYPE_OPTIONS, SD_CAMPAIGN_TYPE_OPTIONS } from "./constants";
import { Checkbox } from "../../../fields/base/Checkbox";
import { HoverInfo } from "../../basic/Tooltip";
import { css } from "../../../../../styled-system/css";

export const ReviewBadge = ({ text }) => (
  <div
    className={css({
      display: "flex",
      gap: "xsmall",
      alignItems: "center",
      borderRadius: "15px",
      paddingY: "xxsmall",
      paddingX: "small",
      color: "white",
      fontSize: "small",
      fontWeight: 600,
      backgroundColor: "trellisPalette.3",
    })}
  >
    {text}
  </div>
);

const SDTargetingOptions = ({ state, errors, onChange, review, fieldsToValidate }) => {
  const { t } = useTranslation(["advertising"]);
  const bidTypeOptions = [
    SD_BID_TYPE_OPTIONS.VCPM,
    SD_BID_TYPE_OPTIONS.CONVERSIONS_CPC,
    SD_BID_TYPE_OPTIONS.PAGE_VISITS_CPC,
  ].map((option) => ({ ...option, label: t(option.label) }));

  const campaignTypeOptions = [SD_CAMPAIGN_TYPE_OPTIONS.AUDIENCE, SD_CAMPAIGN_TYPE_OPTIONS.CONTEXTUAL].map(
    (option) => ({
      ...option,
      label: t(option.label),
    }),
  );

  const updateLookbackPeriod = (value) => {
    const updatedLookbackSelections = toggleArrayEntry(value, state.lookbackPeriod ?? []);
    onChange({
      lookbackPeriod: updatedLookbackSelections,
    });
  };

  if (state.adType !== AD_TYPES.SPONSORED_DISPLAY_V2.value) {
    return null;
  }

  return (
    <Row>
      <Col xs={12} style={{ padding: "1rem" }}>
        <Row>
          <Col xs={12} md={6} style={{ paddingRight: "3rem" }}>
            <div>
              <h3
                style={{
                  fontWeight: 560,
                  fontSize: "1.6rem",
                  color: "#73706E",
                }}
              >
                {t(($) => $.advertising["Bid Type:"])}
                <HoverInfo iconCss={{ color: "grey", display: "inline", paddingStart: "small" }}>
                  {t(
                    ($) =>
                      $.advertising[
                        "vCPM is the cost per one thousand viewable impressions. An advertiser is charged when their ad has been viewed by shoppers, as opposed to a CPC strategy where advertisers pay for ad clicks."
                      ],
                  )}
                </HoverInfo>
              </h3>
            </div>
            <div>
              <FormGroupTile
                type="select"
                stateName={"sdBidType"}
                handleChildFormElement={(key, value) => onChange({ [key]: value.value })}
                disabled={review}
                defaultValue={bidTypeOptions.find((type) => type.value === state.sdBidType)}
                options={bidTypeOptions}
              />
              {errors.sdBidType && fieldsToValidate?.includes("sdBidType") && (
                <div className="mt-3">
                  <small className="text-danger" style={{ paddingLeft: "1rem" }}>
                    {errors.sdBidType}
                  </small>
                </div>
              )}
            </div>
          </Col>
          <Col xs={12} md={6} style={{ paddingLeft: "3rem" }}>
            <div>
              <h3
                style={{
                  fontWeight: 560,
                  fontSize: "1.6rem",
                  color: "#73706E",
                }}
              >
                {t(($) => $.advertising["Campaign Type:"])}
                <HoverInfo iconCss={{ color: "grey", display: "inline", paddingStart: "small" }}>
                  {t(
                    ($) =>
                      $.advertising[
                        "Contextual selects relevant content to serve ads to, while audience targeting delivers ads based on a user's previous browsing history."
                      ],
                  )}
                </HoverInfo>
              </h3>
            </div>
            <div>
              <FormGroupTile
                type="select"
                stateName={"sdCampaignType"}
                handleChildFormElement={(key, value) => {
                  onChange({
                    [key]: value.value,
                    campaignNames: {
                      [value.value === SD_CAMPAIGN_TYPE_OPTIONS.AUDIENCE.value ? "audience_name" : "contextual_name"]:
                        null,
                    },
                  });
                }}
                disabled={review || state.id}
                defaultValue={campaignTypeOptions.find((type) => type.value === state.sdCampaignType)}
                options={campaignTypeOptions}
              />
              {errors.sdCampaignType && fieldsToValidate?.includes("sdCampaignType") && (
                <div className="mt-3">
                  <small className="text-danger" style={{ paddingLeft: "1rem" }}>
                    {errors.sdCampaignType}
                  </small>
                </div>
              )}
            </div>
          </Col>
        </Row>
        {state.sdCampaignType === SD_CAMPAIGN_TYPE_OPTIONS.AUDIENCE.value && (
          <>
            <Row className="mt-5">
              <h3
                style={{
                  fontWeight: 560,
                  fontSize: "1.6rem",
                  color: "#73706E",
                }}
              >
                {t(($) => $.advertising["Lookback Period:"])}{" "}
                <HoverInfo iconCss={{ color: "grey", display: "inline", paddingStart: "small" }}>
                  {t(
                    ($) => $.advertising["Select the period of a user's shopping history used in audience targeting."],
                  )}
                </HoverInfo>
              </h3>
              <p>
                {errors.sdLookbackPeriod && fieldsToValidate?.includes("sdLookbackPeriod") && (
                  <small className="text-danger" style={{ paddingLeft: "1.5rem", fontSize: "12px" }}>
                    {errors.sdLookbackPeriod}
                  </small>
                )}
              </p>
              {review ?
                <div
                  className={css({
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "small",
                  })}
                >
                  {state.lookbackPeriod
                    .sort((a, b) => a - b)
                    .map((period, i) => (
                      <ReviewBadge
                        key={`${period}-${i}`}
                        text={t(($) => $.advertising["{count} Days"], { count: period })}
                      />
                    ))}
                </div>
              : <>
                  <Col xs={12} md={4} lg={2}>
                    <Checkbox
                      label={t(($) => $.advertising["{count} Days"], { count: 7 })}
                      value={state.lookbackPeriod.includes(7)}
                      isDisabled={review}
                      onChange={() => {
                        updateLookbackPeriod(7);
                      }}
                    />
                  </Col>
                  <Col xs={12} md={4} lg={2}>
                    <Checkbox
                      label={t(($) => $.advertising["{count} Days"], { count: 14 })}
                      value={state.lookbackPeriod.includes(14)}
                      isDisabled={review}
                      onChange={() => {
                        updateLookbackPeriod(14);
                      }}
                    />
                  </Col>
                  <Col xs={12} md={4} lg={2}>
                    <Checkbox
                      label={t(($) => $.advertising["{count} Days"], { count: 30 })}
                      value={state.lookbackPeriod.includes(30)}
                      isDisabled={review}
                      onChange={() => {
                        updateLookbackPeriod(30);
                      }}
                    />
                  </Col>
                  <Col xs={12} md={4} lg={2}>
                    <Checkbox
                      label={t(($) => $.advertising["{count} Days"], { count: 60 })}
                      value={state.lookbackPeriod.includes(60)}
                      isDisabled={review}
                      onChange={() => {
                        updateLookbackPeriod(60);
                      }}
                    />
                  </Col>
                  <Col xs={12} md={4} lg={2}>
                    <Checkbox
                      label={t(($) => $.advertising["{count} Days"], { count: 90 })}
                      value={state.lookbackPeriod.includes(90)}
                      isDisabled={review}
                      onChange={() => {
                        updateLookbackPeriod(90);
                      }}
                    />
                  </Col>
                </>
              }
            </Row>
            <Row className={`mt-${review ? "1" : "5"}`}>
              <h3
                style={{
                  fontWeight: 560,
                  fontSize: "1.6rem",
                  color: "#73706E",
                  paddingTop: "1rem",
                }}
              >
                {t(($) => $.advertising["Audience Targeting:"])}{" "}
                <HoverInfo iconCss={{ color: "grey", display: "inline", paddingStart: "small" }}>
                  {t(
                    ($) =>
                      $.advertising["Select how previous user interactions with listings will influence targeting."],
                  )}
                </HoverInfo>
              </h3>
              {errors.sdAudienceTargets && fieldsToValidate?.includes("sdAudienceTargets") && (
                <p>
                  <small className="text-danger" style={{ paddingLeft: "1.5rem", fontSize: "12px" }}>
                    {errors.sdAudienceTargets}
                  </small>
                </p>
              )}
              {!review && (
                <>
                  <Col xs={12} md={5}>
                    <Checkbox
                      label={t(($) => $.advertising["Views Similar Targeting"])}
                      value={state.viewsSimilar}
                      isDisabled={review}
                      onChange={() => {
                        onChange({
                          viewsSimilar: !state.viewsSimilar,
                        });
                      }}
                    />
                  </Col>
                  <Col xs={12} md={5}>
                    <Checkbox
                      label={t(($) => $.advertising["Views Exact Targeting"])}
                      value={state.viewsExact}
                      isDisabled={review}
                      onChange={() => {
                        onChange({
                          viewsExact: !state.viewsExact,
                        });
                      }}
                    />
                  </Col>
                </>
              )}
            </Row>
            {review ?
              <div
                className={css({
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "small",
                })}
              >
                {state.viewsSimilar && <ReviewBadge text={t(($) => $.advertising["Views Similar Targeting"])} />}
                {state.viewsExact && <ReviewBadge text={t(($) => $.advertising["Views Exact Targeting"])} />}
                {state.purchasesSimilar && (
                  <ReviewBadge text={t(($) => $.advertising["Purchases Similar Targeting"])} />
                )}
                {state.purchasesExact && <ReviewBadge text={t(($) => $.advertising["Purchases Exact"])} />}

                {state.sdAmazonSuggestedCategories && (
                  <ReviewBadge text={t(($) => $.advertising["Amazon suggested categories"])} />
                )}
              </div>
            : <>
                <Row style={{ marginTop: "1rem" }}>
                  <Col xs={12} md={5}>
                    <Checkbox
                      label={t(($) => $.advertising["Purchases Similar"])}
                      value={state.purchasesSimilar}
                      isDisabled={review}
                      onChange={() => {
                        onChange({
                          purchasesSimilar: !state.purchasesSimilar,
                        });
                      }}
                    />
                  </Col>
                  <Col xs={12} md={5}>
                    <Checkbox
                      label={t(($) => $.advertising["Purchases Exact"])}
                      value={state.purchasesExact}
                      isDisabled={review}
                      onChange={() => {
                        onChange({
                          purchasesExact: !state.purchasesExact,
                        });
                      }}
                    />
                  </Col>
                </Row>
                <Row style={{ marginTop: "1rem" }}>
                  <Col xs={12} md={5}>
                    <Checkbox
                      label={<span>{t(($) => $.advertising["Amazon suggested categories"])}</span>}
                      value={state.sdAmazonSuggestedCategories}
                      isDisabled={review}
                      onChange={() => {
                        onChange({
                          sdAmazonSuggestedCategories: !state.sdAmazonSuggestedCategories,
                        });
                      }}
                    />
                  </Col>
                </Row>
              </>
            }
          </>
        )}

        {state.sdCampaignType === SD_CAMPAIGN_TYPE_OPTIONS.CONTEXTUAL.value && (
          <Row className="mt-5">
            <h3
              style={{
                fontWeight: 560,
                fontSize: "1.6rem",
                color: "#73706E",
              }}
            >
              {t(($) => $.advertising["Targeting Options:"])}
              <HoverInfo iconCss={{ color: "grey", display: "inline", paddingStart: "small" }}>
                {t(
                  ($) => $.advertising["Select how previous user interactions with listings will influence targeting."],
                )}
              </HoverInfo>
            </h3>
            {errors.sdContextualTargets && fieldsToValidate?.includes("sdContextualTargets") && (
              <p>
                <small className="text-danger" style={{ paddingLeft: "1.5rem", fontSize: "12px" }}>
                  {errors.sdContextualTargets}
                </small>
              </p>
            )}
            {review ?
              <div
                className={css({
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "small",
                })}
              >
                {state.sdDynamicSimilar && <ReviewBadge text={t(($) => $.advertising["Dynamic Similar Targeting"])} />}
                {state.sdAmazonSuggestedCategories && (
                  <ReviewBadge text={t(($) => $.advertising["Amazon suggested categories"])} />
                )}
              </div>
            : <>
                <Col xs={12} md={5}>
                  <Checkbox
                    label={t(($) => $.advertising["Dynamic Similar Targeting"])}
                    value={state.sdDynamicSimilar}
                    isDisabled={review}
                    onChange={() => {
                      onChange({ sdDynamicSimilar: !state.sdDynamicSimilar });
                    }}
                  />
                </Col>
                <Col xs={12} md={5}>
                  <Checkbox
                    label={t(($) => $.advertising["Amazon suggested categories"])}
                    value={state.sdAmazonSuggestedCategories}
                    isDisabled={review}
                    onChange={() => {
                      onChange({
                        sdAmazonSuggestedCategories: !state.sdAmazonSuggestedCategories,
                      });
                    }}
                  />
                </Col>
              </>
            }
          </Row>
        )}
      </Col>
    </Row>
  );
};

export default SDTargetingOptions;
